import React, {
 Fragment, ReactElement, useEffect, useState,
} from 'react'
import styled from 'styled-components'
import Button, { ButtonStyle } from '../buttons/Button'
import Loading from '../components/Loading'
import Modal from '../components/Modal'
import colors from '../lib/colors'
import theme from '../lib/theme'

const Styles = styled.div<{ small?: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  width: fit-content;
  &.switch-label-offset {
    margin-top: 15px;
  }
  ${(props): string => (props.small ? 'font-size: 0.9rem;' : '')}
`
const Label = styled.div`
  transition: color 0.2s;
  color: ${colors.darkerGray};
  margin: 0 4px;
  &.switch-is-on {
    color: ${colors.green};
  }
  user-select: none;
  white-space: nowrap;
`
const Container = styled.div<{ small?: boolean }>`
  position: relative;
  width: ${(props): string => (props.small ? '26px' : '30px')};
  height: ${(props): string => (props.small ? '16px' : '18px')};
`
const Slider = styled.div<{ small?: boolean }>`
  position: absolute;
  ${theme.inset0};
  background-color: ${colors.darkerGray};
  transition: background-color 0.2s;
  border-radius: 20px;
  &:before {
    position: absolute;
    content: '';
    height: ${(props): string => (props.small ? '12px' : '14px')};
    width: ${(props): string => (props.small ? '12px' : '14px')};
    left: 2px;
    bottom: 2px;
    background-color: ${colors.white};
    transition: transform 0.2s;
    border-radius: 50%;
  }
  &.switch-disabled {
    &:before {
      background-color: ${colors.gray};
    }
  }
  &.switch-is-on {
    background-color: ${colors.green};
    :before {
      transform: ${(props): string => (props.small ? 'translateX(10px)' : 'translateX(12px)')};
    }
  }
  &.switch-loading {
    :before {
      transform: ${(props): string => (props.small ? 'translateX(5px)' : 'translateX(6px)')};
    }
  }
`
const StyledLoading = styled(Loading)`
  transform: scale(0.7);
  margin: 0 4px;
  color: ${colors.darkerGray};
`
const ModalContents = styled.div`
  p {
    margin: 10px auto 0;
  }
  min-width: 200px;
  max-width: 360px;
  text-align: center;
  font-weight: bold;
`
const ButtonWrapper = styled.div`
  padding-top: 10px;
  button {
    margin: 10px;
    padding: 5px;
  }
`

export interface ISwitchProps {
  label?: string
  flipLabel?: boolean
  value: boolean
  switchAction: ((isOn: boolean) => void) | ((isOn: boolean) => Promise<void>)
  disabled?: boolean
  className?: string
  labelOffset?: boolean
  showConfirmation?: boolean
  confirmationMessage?: string
  small?: boolean
}

function Switch(props: ISwitchProps): ReactElement {
  const {
    label,
    flipLabel,
    value,
    switchAction,
    disabled,
    className,
    labelOffset,
    showConfirmation,
    confirmationMessage,
    small,
  } = props
  const [isOn, setIsOn] = useState<boolean>(value)
  const [loading, setLoading] = useState<boolean>(false)

  // if we change the value externally, update the switch
  // to match new value without triggering the action
  useEffect(() => {
    setIsOn(value)
  }, [value])

  const renderLabel = label
    && (!loading ? (
      <Label className={isOn ? 'switch-is-on' : 'switch-is-off'}>{label}</Label>
    ) : (
      <StyledLoading />
    ))

  const sliderClassNames = `${isOn ? 'switch-is-on' : 'switch-is-off'} ${disabled ? 'switch-disabled' : ''
    }  ${loading ? 'switch-loading' : ''}`

  const [confirmationShowing, setConfirmationShowing] = useState<boolean>(false)

  const handleClick = async (): Promise<void> => {
    setLoading(true)
    await switchAction(!isOn)
    setIsOn((prevState) => !prevState)
    setLoading(false)
  }

  const onConfirmationClick = async (): Promise<void> => {
    setConfirmationShowing(false)
    await handleClick()
  }

  return (
    <Fragment>
      <Styles
        className={`${className} ${labelOffset ? 'switch-label-offset' : ''}`}
        onClick={async (): Promise<void> => {
          if (!disabled) {
            if (showConfirmation) {
              setConfirmationShowing(true)
            } else {
              await handleClick()
            }
          }
        }}
        small={small}
      >
        {!flipLabel && renderLabel}
        <Container small={small}>
          <Slider className={sliderClassNames} small={small} />
        </Container>
        {flipLabel && renderLabel}
      </Styles>
      {confirmationShowing && (
        <Modal
          modalOpen={confirmationShowing}
          onModalClose={(): void => setConfirmationShowing(false)}
          modalAction={onConfirmationClick}
        >
          <ModalContents>
            <p>{confirmationMessage || 'Are you sure?'}</p>
            <ButtonWrapper>
              <Button
                buttonStyle={ButtonStyle.Danger}
                onClick={onConfirmationClick}
              >
                Yes
              </Button>
              <Button
                buttonStyle={ButtonStyle.Secondary}
                onClick={(): void => setConfirmationShowing(false)}
              >
                No
              </Button>
            </ButtonWrapper>
          </ModalContents>
        </Modal>
      )}
    </Fragment>
  )
}
export default Switch
